// Portfolio › Brief (FOUNDER-OS; 12 "Founder brief — generated from the Focus layer"): the latest
// brief (its headline, then What's off, What changed, Decisions waiting, Coming up — each line in
// its tone, with its company chip), the earlier briefs, and "Generate daily" / "Generate weekly".
import { Badge, Button, CompanyTile, cn, LocationHeader } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { NewspaperIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { FounderBrief, FounderBriefLine } from "../../api/types";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { LoadError, Loading, Nothing } from "../screen-parts";
import { briefsQuery, focusQuery, portfolioKeys, useCanEditPortfolio } from "./portfolio-queries";

const TONE: Record<FounderBriefLine["tone"], string> = {
  bad: "border-l-destructive bg-danger-soft/40",
  good: "border-l-success bg-success-soft/40",
  neutral: "border-l-border-strong",
};

export function PortfolioBriefScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const briefs = useQuery(briefsQuery(api, company.id));
  const focus = useQuery(focusQuery(api, company.id));
  // An owner of the whole account generates (a grant limited to some companies reads only).
  const owner = useCanEditPortfolio(api, company.id);
  const qc = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);
  const generate = useMutation({
    mutationFn: (cadence: "daily" | "weekly") =>
      api.client.portfolio.briefs.generate({ params: { co: company.id }, body: { cadence } }),
    onSuccess: async (b) => {
      setPicked(b.id);
      await qc.invalidateQueries({ queryKey: portfolioKeys.briefs(company.id) });
    },
  });
  const list = briefs.data?.data ?? [];
  const shown = list.find((b) => b.id === picked) ?? list[0] ?? null;
  const names = new Map((focus.data?.companies ?? []).map((r) => [r.company_id, r.name]));
  return (
    <div className="flex min-w-0 flex-1 flex-col" data-slot="portfolio-brief">
      <LocationHeader
        icon={NewspaperIcon}
        crumbs={[
          {
            label: "Portfolio",
            render: <Link to="/c/$slug/portfolio" params={{ slug: company.slug }} />,
          },
        ]}
        title="Brief"
        actions={
          owner ? (
            <>
              <Button
                size="sm"
                variant="ghost"
                disabled={generate.isPending}
                onClick={() => generate.mutate("weekly")}
              >
                Generate weekly
              </Button>
              <Button
                size="sm"
                disabled={generate.isPending}
                onClick={() => generate.mutate("daily")}
              >
                <RefreshCwIcon aria-hidden="true" />
                Generate daily
              </Button>
            </>
          ) : null
        }
      />
      <div className="flex min-w-0 gap-6 px-6 py-5 max-lg:flex-col max-md:px-4">
        <div className="flex min-w-0 flex-1 flex-col">
          {briefs.isPending ? (
            <Loading label="Loading the briefs" />
          ) : briefs.isError ? (
            <LoadError what="The briefs" error={briefs.error} />
          ) : shown ? (
            <BriefView brief={shown} names={names} />
          ) : (
            <Nothing title="No brief yet">
              Generate one: it reads the Focus layer as it is now.
            </Nothing>
          )}
        </div>
        {list.length > 0 ? (
          <aside aria-labelledby="earlier" className="w-64 shrink-0 max-lg:w-full">
            <h2
              id="earlier"
              className="mb-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              Briefs
            </h2>
            <ul className="flex flex-col gap-1">
              {list.map((b) => (
                <li key={b.id}>
                  <button
                    type="button"
                    aria-current={b.id === shown?.id ? "true" : undefined}
                    onClick={() => setPicked(b.id)}
                    className="flex w-full flex-col items-start gap-0.5 rounded-md px-2.5 py-2 text-left text-sm hover:bg-muted aria-[current=true]:bg-primary-soft aria-[current=true]:text-primary-text"
                  >
                    <span className="flex items-center gap-1.5 font-medium">
                      {b.cadence === "weekly" ? "Weekly" : "Daily"} · {b.period_key}
                    </span>
                    <span className="line-clamp-1 text-xs text-muted-foreground">{b.headline}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>
    </div>
  );
}

function BriefView({ brief, names }: { brief: FounderBrief; names: Map<string, string> }) {
  return (
    <article aria-labelledby="brief-headline" className="flex flex-col gap-5" data-slot="brief">
      <header className="flex flex-col gap-1">
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          <Badge variant="outline">{brief.cadence === "weekly" ? "Weekly" : "Daily"}</Badge>
          {brief.period_key} · {formatDateTime(brief.created_at)}
          {brief.generated_by === "agent" ? " · by the founder crew" : ""}
        </span>
        <h2 id="brief-headline" className="font-heading text-xl font-semibold text-foreground">
          {brief.headline}
        </h2>
      </header>
      {brief.sections.map((s) => (
        <section
          key={s.key}
          aria-label={s.title}
          data-section={s.key}
          className="flex flex-col gap-2"
        >
          <h3 className="text-sm font-semibold text-foreground">{s.title}</h3>
          <ul className="flex flex-col gap-1.5">
            {s.lines.map((l, i) => {
              const company = l.company_id ? names.get(l.company_id) : null;
              // The chip names the company: the line drops its "Acme Recruiting: " (or
              // "Acme Recruiting · ") lead.
              const text =
                company && (l.text.startsWith(`${company}: `) || l.text.startsWith(`${company} · `))
                  ? l.text.slice(company.length + (l.text[company.length] === ":" ? 2 : 3))
                  : l.text;
              return (
                <li
                  // biome-ignore lint/suspicious/noArrayIndexKey: a brief's lines are fixed
                  key={i}
                  data-tone={l.tone}
                  className={cn(
                    "flex items-center gap-2 rounded-r-md border-l-[3px] px-3 py-1.5 text-sm",
                    TONE[l.tone],
                  )}
                >
                  {company ? (
                    <span className="flex shrink-0 items-center gap-1 rounded-sm bg-card px-1 py-0.5 text-2xs font-medium text-secondary">
                      <CompanyTile name={company} size={20} />
                      {company}
                    </span>
                  ) : null}
                  <span className="min-w-0">{text}</span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </article>
  );
}
